import {
  useQuery,
  UseQueryResult,
  UseQueryOptions,
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from 'react-query';
import { IntegrationField } from 'api/integrations';
import {
  HubspotAPI,
  HubspotConfig,
  HubspotFieldMapping,
  HubspotIntegrationProvider,
  HubspotObject,
  HubspotResourceCreatePayload,
  HubspotResourceMapping,
} from 'api/integrations/hubspot';
import { LinkedResource } from 'api/tasks';

export const HUBSPOT_CONFIG_QUERY_KEY = 'getHubspotConfig';
export const HUBSPOT_AUTH_QUERY_KEY = 'hubspotAuthenticationDetails';
export const HUBSPOT_RESOURCE_MAPPINGS_QUERY_KEY = 'hubspotResourceMappings';
export const HUBSPOT_EXTERNAL_RESOURCE_FIELDS_QUERY_KEY =
  'hubspotExternalResourceFields';

export const useDisconnectHubspotMutation = (): UseMutationResult<
  void,
  unknown,
  number
> => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, number>(
    (integrationId) => {
      return HubspotAPI.disconnectIntegration(integrationId);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(HUBSPOT_CONFIG_QUERY_KEY);
      },
    },
  );
};

export const useHubspotAuthenticateQuery = (
  options: UseQueryOptions<
    HubspotIntegrationProvider,
    unknown,
    HubspotIntegrationProvider,
    [typeof HUBSPOT_AUTH_QUERY_KEY]
  > = {},
): UseQueryResult<HubspotIntegrationProvider, unknown> => {
  return useQuery([HUBSPOT_AUTH_QUERY_KEY], () => HubspotAPI.getAuthDetails(), {
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useHubspotConfigQuery = (
  options: UseQueryOptions<
    HubspotConfig[],
    unknown,
    HubspotConfig[],
    typeof HUBSPOT_CONFIG_QUERY_KEY
  > = {},
): UseQueryResult<HubspotConfig[], unknown> => {
  return useQuery(
    HUBSPOT_CONFIG_QUERY_KEY,
    () => HubspotAPI.getIntegrationConfig(),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 0,
      ...options,
    },
  );
};

export const useHubspotConfigByIdQuery = (
  integrationId: number,
  options: UseQueryOptions<
    HubspotConfig,
    unknown,
    HubspotConfig,
    [typeof HUBSPOT_CONFIG_QUERY_KEY, number]
  > = {},
): UseQueryResult<HubspotConfig, unknown> => {
  return useQuery(
    [HUBSPOT_CONFIG_QUERY_KEY, integrationId],
    () => HubspotAPI.getIntegrationConfigById(integrationId),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 0,
      ...options,
    },
  );
};

export const useHubspotCreateResourceMutation = (
  integrationId: number,
): UseMutationResult<
  HubspotResourceMapping,
  unknown,
  HubspotResourceCreatePayload
> => {
  const queryClient = useQueryClient();

  return useMutation<
    HubspotResourceMapping,
    unknown,
    HubspotResourceCreatePayload
  >(
    (payload) => {
      return HubspotAPI.createResourceObject(integrationId, payload);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          HUBSPOT_RESOURCE_MAPPINGS_QUERY_KEY,
        );
      },
    },
  );
};

export const useHubspotExternalResourceFields = (
  integrationId: number,
  externalResourceType: string,
  options: UseQueryOptions<
    IntegrationField[],
    unknown,
    IntegrationField[],
    [typeof HUBSPOT_EXTERNAL_RESOURCE_FIELDS_QUERY_KEY, number, string]
  > = {},
): UseQueryResult<IntegrationField[], unknown> =>
  useQuery(
    [
      HUBSPOT_EXTERNAL_RESOURCE_FIELDS_QUERY_KEY,
      integrationId,
      externalResourceType,
    ],
    () =>
      HubspotAPI.getExternalResourceFields(integrationId, externalResourceType),
    { retry: 1, refetchOnWindowFocus: false, ...options },
  );

export const useHubspotDeleteMappingMutation = (
  integrationId: number,
): UseMutationResult<
  void,
  unknown,
  { resourceId: number; mappingId: number }
> => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, { resourceId: number; mappingId: number }>(
    (payload: { resourceId: number; mappingId: number }) => {
      return HubspotAPI.deleteFieldMapping(
        integrationId,
        payload.resourceId,
        payload.mappingId,
      );
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          HUBSPOT_RESOURCE_MAPPINGS_QUERY_KEY,
        );
      },
    },
  );
};

export const useHubspotDeleteResourceMutation = (
  integrationId: number,
): UseMutationResult<void, unknown, number> => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, number>(
    (resourceId: number) => {
      return HubspotAPI.deleteResourceObject(integrationId, resourceId);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          HUBSPOT_RESOURCE_MAPPINGS_QUERY_KEY,
        );
      },
    },
  );
};

export const HUBSPOT_EXTERNAL_RESOURCE_TYPES_QUERY_KEY =
  'hubspotExternalResourceTypes';

export const useHubspotExternalResourceTypes = (
  integrationId: number,
  options: UseQueryOptions<
    HubspotObject[],
    unknown,
    HubspotObject[],
    [typeof HUBSPOT_EXTERNAL_RESOURCE_TYPES_QUERY_KEY, number]
  > = {},
): UseQueryResult<HubspotObject[], unknown> =>
  useQuery(
    [HUBSPOT_EXTERNAL_RESOURCE_TYPES_QUERY_KEY, integrationId],
    () => HubspotAPI.getExternalResourceTypes(integrationId),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      ...options,
    },
  );

type CreatePayload = {
  resourceId: number;
  mappingData: HubspotFieldMapping;
};

export const useHubspotMappingCreateMutation = (
  integrationId: number,
): UseMutationResult<HubspotFieldMapping, unknown, CreatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<HubspotFieldMapping, unknown, CreatePayload>(
    (payload: CreatePayload) =>
      HubspotAPI.createFieldMapping(
        integrationId,
        payload.resourceId,
        payload.mappingData,
      ),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          HUBSPOT_RESOURCE_MAPPINGS_QUERY_KEY,
        );
      },
    },
  );
};

type UpdatePayload = {
  resourceId: number;
  fieldMappingId: number;
  mappingData: HubspotFieldMapping;
};

export const useHubspotMappingUpdateMutation = (
  integrationId: number,
): UseMutationResult<HubspotFieldMapping, unknown, UpdatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<HubspotFieldMapping, unknown, UpdatePayload>(
    (payload: UpdatePayload) =>
      HubspotAPI.updateFieldMapping(
        integrationId,
        payload.resourceId,
        payload.fieldMappingId,
        payload.mappingData,
      ),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          HUBSPOT_RESOURCE_MAPPINGS_QUERY_KEY,
        );
      },
    },
  );
};

export const useHubspotResourceMappings = (
  integrationId: number,
  options: UseQueryOptions<
    HubspotResourceMapping[],
    unknown,
    HubspotResourceMapping[],
    [typeof HUBSPOT_RESOURCE_MAPPINGS_QUERY_KEY, number]
  > = {},
): UseQueryResult<HubspotResourceMapping[], unknown> =>
  useQuery(
    [HUBSPOT_RESOURCE_MAPPINGS_QUERY_KEY, integrationId],
    () => HubspotAPI.getResourceMappings(integrationId),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
export const HUBSPOT_MAPPABLE_NATIVE_RESOURCE_FIELDS_QUERY_KEY =
  'hubspotMappableNativeResourceFields';

export const useHubspotMappableNativeResourceFields = (
  integrationId: number,
  nativeResourceType: string,
  externalResourceType: string,
  externalResourceFieldId: string,
  options: UseQueryOptions<
    IntegrationField[],
    unknown,
    IntegrationField[],
    [
      typeof HUBSPOT_MAPPABLE_NATIVE_RESOURCE_FIELDS_QUERY_KEY,
      number,
      string,
      string,
      string,
    ]
  > = {},
): UseQueryResult<IntegrationField[], unknown> =>
  useQuery(
    [
      HUBSPOT_MAPPABLE_NATIVE_RESOURCE_FIELDS_QUERY_KEY,
      integrationId,
      nativeResourceType,
      externalResourceType,
      externalResourceFieldId,
    ],
    () =>
      HubspotAPI.getMappableNativeResourceFields(
        integrationId,
        nativeResourceType,
        externalResourceType,
        externalResourceFieldId,
      ),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      ...options,
    },
  );

export const HUBSPOT_UN_LINK_OBJECT_KEY = 'hubspotUnLinkObjectKey';

export const useHubspotUnLinkObjectQuery = (
  integrationId: number,
  resourceMappingId: number,
  options: UseQueryOptions<
    HubspotObject,
    unknown,
    HubspotObject,
    [typeof HUBSPOT_UN_LINK_OBJECT_KEY, number]
  > = {},
) =>
  useQuery(
    [HUBSPOT_UN_LINK_OBJECT_KEY, integrationId],
    () =>
      HubspotAPI.unLinkHubspotObjectWithProject(
        integrationId,
        resourceMappingId,
      ),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      ...options,
    },
  );

export const HUBSPOT_GET_LINK_OBJECT_KEY = 'hubspotGetLinkObjectKey';

export const useHubspotGetLinkObjectQuery = (
  integrationId?: number,
  externalResourceId?: string,
  externalResourceType?: string,
  options: UseQueryOptions<
    HubspotObject,
    unknown,
    HubspotObject,
    [
      typeof HUBSPOT_GET_LINK_OBJECT_KEY,
      number | undefined,
      string | undefined,
      string | undefined,
    ]
  > = {},
) =>
  useQuery(
    [
      HUBSPOT_GET_LINK_OBJECT_KEY,
      integrationId,
      externalResourceId,
      externalResourceType,
    ],
    () =>
      HubspotAPI.getHubspotObjectWithProject(
        integrationId,
        externalResourceId,
        externalResourceType,
      ),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      ...options,
    },
  );

type LinkObjectPayload = {
  integrationId: number;
  projectId: number;
  hubspotObjectUrl: string;
};

type ErrorData = {
  response: {
    data: {
      errors: Array<{
        reason: string;
      }>;
    };
  };
};

export const HUBSPOT_LINK_OBJECT_KEY = 'hubspotLinkObjectKey';

export const useHubspotLinkObjectMutation = (
  options: Pick<
    UseMutationOptions<LinkedResource, ErrorData, LinkObjectPayload>,
    'onSuccess' | 'onError'
  >,
) => {
  return useMutation<LinkedResource, ErrorData, LinkObjectPayload>({
    mutationFn: (data: LinkObjectPayload) =>
      HubspotAPI.linkHubspotObject(data.integrationId, {
        projectId: data.projectId,
        hsObjectUrl: data.hubspotObjectUrl,
      }),

    ...options,
  });
};
